import { Eye, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SelectorVerComo } from "@/components/ui/selector-ver-como";
import { useSociosListado } from "@/hooks/usePortalSocios";
import { useSocioImpersonation } from "./SocioImpersonationContext";

/**
 * "Ver como socio" (sólo Super Admin), pintado con el estándar `SelectorVerComo`.
 * Fuente: get_socios_listado().
 */
export function SocioImpersonationSelector({ className }: { className?: string }) {
  const { canImpersonate, target, setTarget, clear } = useSocioImpersonation();
  const listado = useSociosListado(canImpersonate);

  if (!canImpersonate) return null;

  const socios = (listado.data?.data ?? [])
    .filter((s) => !!(s.email ?? s.email_usuario))
    .slice()
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

  const textoVacio = listado.data?.pendiente
    ? "El listado de socios aún no está disponible en esta BD."
    : listado.data?.sinAcceso
      ? "Sin permiso para ver el listado de socios."
      : "No se encontró el socio.";

  return (
    <SelectorVerComo
      className={className}
      etiqueta={target ? target.nombre : "Mi vista"}
      impersonando={!!target}
      iconoVistaPropia={<UserRound className="size-3.5" strokeWidth={1.75} />}
      opcionPorDefecto={{
        key: "__mi_vista__",
        label: "Mi vista",
        nota: "Por defecto",
        seleccionada: !target,
        onSelect: clear,
        icono: <UserRound className="mr-2 size-3.5 shrink-0 text-primary" />,
      }}
      grupos={[{
        titulo: "Socios",
        opciones: socios.map((s) => {
          // Correo de la PERSONA (el mismo que muestra Personas → Socios); la cuenta solo si no tiene.
          const email = (s.email ?? s.email_usuario) as string;
          return {
            key: `${s.id_persona}-${email}`,
            label: s.nombre || email,
            // El buscador filtra por nombre, emails y proyectos.
            buscarPor: `${s.email ?? ""} ${s.email_usuario ?? ""} ${s.proyectos.join(" ")} #${s.id_persona}`,
            nota: !s.rol_socio_activo ? "Sin rol" : s.proyectos[0],
            seleccionada: target?.email.toLowerCase() === email.toLowerCase(),
            onSelect: () => setTarget({ id_persona: s.id_persona, nombre: s.nombre || email, email }),
          };
        }),
      }]}
      onLimpiar={clear}
      ariaLimpiar="Dejar de ver como socio"
      placeholderBusqueda="Buscar por nombre, email o proyecto..."
      textoVacio={textoVacio}
      cargando={listado.isLoading}
    />
  );
}

/** Banner persistente mientras se impersona. */
export function SocioImpersonationBanner() {
  const { target, clear } = useSocioImpersonation();
  if (!target) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-500/30 bg-amber-50 px-4 py-2 text-[13px] text-amber-900 sm:px-6 dark:bg-amber-500/10 dark:text-amber-200"
    >
      <Eye className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        Viendo como <span className="font-semibold">{target.nombre}</span>
        <span className="hidden sm:inline"> · {target.email}</span>
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={clear}
        className="h-7 border-amber-500/40 bg-transparent px-2.5 text-[12px] text-amber-900 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-500/20"
      >
        Salir
      </Button>
    </div>
  );
}
